import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { camelCaseToTitleCase, numberToDecimalWithCurrency } from "@/lib/utils";
import { VolumeApi } from "@/lib/volume-api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AlertDialog, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useState, type ElementType } from "react";
import { hasRole } from "@/lib/auth";
import { GetPaymentDetails } from "@/types/getPaymentDetails";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  TriangleAlert,
  Lock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ConfirmationOfPayerInfoModal } from "./-components/confirmation-of-payer-info-modal";
import { PayerValidationBadge } from "../-components/payments_$paymentId/payer-validation-badge";
import { StatusField } from "./-components/status-field";
import { RefundForm } from "./-components/refund-form";

export const Route = createFileRoute(
  "/(app)/_main-layout/_payments/payments_/$paymentId",
)({
  component: () => <PaymentComponent />,
  loader: ({ params, context }) => {
    const api = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    return api.getPaymentDetails({ paymentId: params.paymentId });
  },
  onError(err: Error) {
    if (err.message === "Failed to fetch data") {
      throw redirect({
        to: "/payments/payin",
        search: (prev) => ({ applicationId: prev.applicationId }),
      });
    }
  },
});

function StatusDetails({ payment }: { payment: GetPaymentDetails }) {
  // Map status to description and icon/color
  const statusConfig: Record<
    string,
    {
      icon: ElementType;
      color: string;
      bg: string;
      border: string;
      label: string;
    }
  > = {
    NEW: {
      icon: Clock,
      color: "text-teal-600",
      bg: "bg-teal-200",
      border: "border-teal-300",
      label: "New",
    },
    AWAITING_AUTHORIZATION: {
      icon: Clock,
      color: "text-fuchsia-600",
      bg: "bg-fuchsia-200",
      border: "border-fuchsia-300",
      label: "Awaiting Authorisation",
    },
    AUTHORIZED: {
      icon: CheckCircle,
      color: "text-blue-600",
      bg: "bg-blue-200",
      border: "border-blue-300",
      label: "Authorized",
    },
    PENDING: {
      icon: RefreshCw,
      color: "text-cyan-600",
      bg: "bg-cyan-200",
      border: "border-cyan-300",
      label: "Pending",
    },
    COMPLETED: {
      icon: CheckCircle,
      color: "text-green-600",
      bg: "bg-green-200",
      border: "border-green-300",
      label: "Completed",
    },
    SETTLED: {
      icon: CheckCircle,
      color: "text-green-600",
      bg: "bg-green-200",
      border: "border-green-300",
      label: "Settled",
    },
    FAILED: {
      icon: XCircle,
      color: "text-red-600",
      bg: "bg-red-200",
      border: "border-red-300",
      label: "Failed",
    },
  };

  const statusDescriptions: Record<string, string> = {
    AWAITING_AUTHORIZATION:
      "The payer has not provided authorisation for this payment to be completed to their bank. Awaiting payer authorisation.",
    PENDING:
      "The payer has successfully authorised this payment in their bank app. The bank is now in the process of initiating this payment.",
    COMPLETED: "Payment has been sent by the payer's bank",
    SETTLED:
      "Payment has been received and settled into your virtual account, and will appear in your account balance.",
    FAILED: "Payment has failed.",
    NEW: "The payment has been created and is awaiting further action.",
    AUTHORIZED: "The payment has been authorised by the payer.",
  };

  const status = payment.paymentStatus;
  const details = statusDescriptions[status] || "";
  const config = statusConfig[status] || statusConfig["NEW"];
  const StatusIcon = config.icon;

  return (
    <Card className={`rounded-2xl ${config.bg} ${config.border}`}>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <StatusIcon className={`w-6 h-6 pt-1 ${config.color}`} />
        <CardTitle className="text-lg">Status Details</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="mb-2 text-base font-semibold flex items-center gap-2">
          <span className={config.color}>{config.label}</span>
        </div>
        <div className="text-sm text-V_Base90 mb-2">{details}</div>
        {status === "FAILED" && payment.errorDetails && (
          <div className="mt-2">
            <span className="font-semibold">Error Details:</span>
            <div className="text-sm text-red-700 mt-1 whitespace-pre-line">
              {payment.errorDetails}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ComplianceHoldCard({
  heldStartedAt,
  isHeld,
}: {
  heldStartedAt: string;
  isHeld: boolean;
}) {
  const heldAtDate = new Date(heldStartedAt);
  const heldStartedAtDisplay = heldAtDate.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZone: "UTC",
    hour12: false,
  });
  const cardColor = "bg-amber-50 border-amber-200 text-amber-800";
  const title = "Compliance Hold";
  const subtitle = `Hold started at ${heldStartedAtDisplay} UTC`;
  return (
    <Card className={`rounded-2xl ${cardColor}`}>
      <CardHeader className="pb-2 gap-2">
        <div className="flex items-center gap-2">
          <TriangleAlert className="w-6 h-6" />
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-base">
          This payment {isHeld ? "is currently" : "was"} held for additional
          compliance checks.
        </div>
        <div className="text-base mt-2">{subtitle}</div>
      </CardContent>
    </Card>
  );
}

function PaymentComponent() {
  const payment = Route.useLoaderData();
  const context = Route.useRouteContext();
  const [open, setOpen] = useState(false);

  const date = new Date(payment.createdAt);
  // probs convert this to moment, sort spacing
  const dateDisplay = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

  return (
    <>
      <div className="px-3">
        <Link
          to="/payments/payin"
          search
          className="inline-flex items-center gap-2 text-sm text-volume-text-purple font-medium"
        >
          <span aria-hidden="true">←</span>{" "}
          <span className="hover:underline">Back to Payments</span>
        </Link>
      </div>
      <div className="my-2">
        <div className="rounded-2xl bg-white ">
          <div className="w-full rounded-t-2xl bg-[#E8EAFF] p-6 font-bold text-volume-text-purple">
            <h5 className="flex">Summary</h5>
          </div>
          <div className="border-b-2 border-volume-grey bg-white px-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Merchant Payment ID</TableHead>
                  <TableHead>Date and Time (UTC)</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">
                    {numberToDecimalWithCurrency(
                      payment.paymentRequest.amount,
                      payment.paymentRequest.currency,
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusField payment={payment} />
                  </TableCell>
                  <TableCell>{payment.paymentId}</TableCell>
                  <TableCell>{payment.paymentRequest.reference}</TableCell>
                  <TableCell>{payment.merchantPaymentId}</TableCell>
                  <TableCell>{dateDisplay}</TableCell>
                  <TableCell>
                    <AlertDialog
                      open={open}
                      onOpenChange={(newOpen) => {
                        setOpen(newOpen);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          disabled={
                            !(
                              payment.canStartRefund &&
                              hasRole("INITIATE_REFUNDS", context.authorities)
                            )
                          }
                          variant="outline"
                          size="sm"
                        >
                          Refund
                        </Button>
                      </AlertDialogTrigger>
                      <RefundForm payment={payment} setDialogOpen={setOpen} />
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Status Details Section */}
          <div className="px-6 pt-4">
            <StatusDetails payment={payment} />
          </div>

          {/* Compliance Hold Card (separate card below) */}
          {payment.heldStartedAt && (
            <div className="px-6 pt-4">
              <ComplianceHoldCard
                heldStartedAt={payment.heldStartedAt}
                isHeld={payment.isHeld}
              />
            </div>
          )}

          {/* Payer Details and Payment Metadata Side by Side */}
          <div className="px-6 pb-6 pt-4">
            <div className="grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2  gap-16">
              {/* Payer Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Payer Details</h3>
                <div>
                  <PayerDetailsRow k={"Name"} value={payment.payer?.name} />
                  <PayerDetailsRow
                    k={"Institution ID"}
                    value={payment.institutionId}
                  />
                  <PayerDetailsRow
                    k={"Sort Code"}
                    value={payment.payer?.accountIdentifier.sortCode}
                  />
                  <PayerDetailsRow
                    k={"Account Number"}
                    value={payment.payer?.accountIdentifier.accountNumber}
                  />
                  <PayerDetailsRow
                    k={"IBAN"}
                    value={payment.payer?.accountIdentifier.iban}
                  />
                  <PayerDetailsRow
                    k={"BIC"}
                    value={payment.payer?.accountIdentifier.bic}
                  />
                  <PayerDetailsRow k="IP Address" value={payment.payerIP} />
                </div>
              </div>
              <div className="relative col-span-2">
                <div className="grid grid-cols-2 gap-16">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Payer KYC Details
                    </h3>
                    <div>
                      {Object.entries(
                        payment.merchantProvidedPayerDetails?.address ?? {},
                      ).map(([key, value]) => {
                        return (
                          <PayerDetailsRow
                            k={camelCaseToTitleCase(key)}
                            value={value ?? " "}
                          />
                        );
                      })}
                      {Object.entries(
                        payment.merchantProvidedPayerDetails?.account ?? {},
                      ).map(([key, value]) => {
                        return (
                          <PayerDetailsRow
                            k={camelCaseToTitleCase(key)}
                            value={value}
                          />
                        );
                      })}
                      {!payment.confirmationOfPayer.enabled &&
                        !payment.merchantProvidedPayerDetails && (
                          <>
                            <PayerDetailsRow k={"Name"} value={" "} />
                            <PayerDetailsRow k={"Phone"} value={" "} />
                            <PayerDetailsRow k={"Email"} value={" "} />
                            <PayerDetailsRow k={"Account Number"} value={" "} />
                            <PayerDetailsRow k={"Sort Code"} value={" "} />
                            <PayerDetailsRow k={"IBAN"} value={" "} />
                            <PayerDetailsRow k={"Address"} value={" "} />
                          </>
                        )}
                      {payment.confirmationOfPayer.enabled &&
                        !payment.merchantProvidedPayerDetails && (
                          <Badge className="text-center bg-gray-500 text-white">
                            No KYC details available
                          </Badge>
                        )}
                    </div>
                  </div>
                  <div className="">
                    <h3 className="text-lg font-semibold mb-4">
                      Payer Validation
                    </h3>
                    <div>
                      <PayerValidationBadge
                        payerInfo={payment.confirmationOfPayer}
                      />
                    </div>
                    {!payment.confirmationOfPayer.enabled && (
                      <div
                        className="absolute backdrop-blur-sm rounded-xl flex items-center justify-center"
                        style={{
                          left: "20%",
                          width: "80%",
                          top: "2rem",
                          bottom: 0,
                          backgroundColor: "#D9D9D982",
                        }}
                      >
                        <div className="flex items-center gap-2 text-purple-600">
                          <Lock className="w-4 h-4" />
                          <ConfirmationOfPayerInfoModal />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Metadata */}
              {payment.metadata ? (
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Payment Metadata
                  </h3>
                  <div>
                    {Object.entries(payment.metadata).map(([key, value]) => {
                      // value is json, can be string, array, or deeply nested object
                      if (
                        typeof value === "string" ||
                        typeof value === "number"
                      ) {
                        return (
                          <div key={key} className="flex justify-between mb-2">
                            <div className="font-normal text-sm text-V_Base90 flex flex-col gap-3">
                              <span>
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </span>
                            </div>
                            <div className="font-semibold text-V_Base90 text-sm flex flex-col gap-3 text-right">
                              <span>{value}</span>
                            </div>
                          </div>
                        );
                      }
                      // just stringify pretty print the json
                      return (
                        <div key={key} className="flex justify-between mb-2">
                          <div className="font-normal text-sm text-V_Base90 flex flex-col gap-3">
                            <span>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </span>
                          </div>
                          <Accordion type="single" collapsible>
                            <AccordionItem value="item-1">
                              <AccordionTrigger
                                className="font-extralight min-w-[50px]"
                                style={{ padding: 0 }}
                              >
                                <div>
                                  {Array.isArray(value) ? "[ ... ]" : "{ ... }"}
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div>
                                  <pre>{JSON.stringify(value, null, 2)}</pre>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PayerDetailsRow({ k, value }: { k: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex justify-between mb-3">
      <div className="font-normal text-sm text-V_Base90 flex flex-col gap-3">
        <span>{k}</span>
      </div>
      <div className="font-semibold text-V_Base90 text-sm flex flex-col gap-3 text-right">
        <span>{value}</span>
      </div>
    </div>
  );
}
